import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DEFAULT_GRACE_DAYS, ERASURE_NOTE_MAX, erasureCreateSchema } from '@today/models'
import { ErasureBasis } from '@today/types'
import type { ErasureFormValues, ErasureRequestFormViewProps } from '@today/ui-domain'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { COMPLIANCE_NAMESPACE } from '@/i18n'
import { complianceService } from '@/services/compliance.service'
import { complianceError } from './compliance-errors'
import { ERASURES_KEY } from './useErasureList'

const INITIAL: ErasureFormValues = {
	customerId: '',
	basis: ErasureBasis.CUSTOMER_REQUEST,
	graceDays: String(DEFAULT_GRACE_DAYS),
	note: '',
}
const MAX_GRACE_DAYS = 90
type Errors = ErasureRequestFormViewProps['errors']

/** The local checks that spare an API round trip (REQ-202); the API re-validates everything. */
const validate = (values: ErasureFormValues, t: (key: string) => string): Errors => {
	const errors: Errors = {}
	if (!erasureCreateSchema.shape.customerId.safeParse(values.customerId.trim()).success) {
		errors.customerId = t('validation.customerId')
	}
	const graceDays = Number(values.graceDays)
	if (!/^\d+$/.test(values.graceDays.trim()) || graceDays < 1 || graceDays > MAX_GRACE_DAYS) {
		errors.graceDays = t('validation.graceDays')
	}
	if (values.note.trim().length > ERASURE_NOTE_MAX) errors.note = t('validation.note')
	return errors
}

/**
 * The "New erasure request" form (TO-232 compliance-admin-ui REQ-202). On 201 the
 * created request's panel opens; a refusal keeps the values and shows its message.
 */
export function useErasureForm(onCreated: (id: string) => void): ErasureRequestFormViewProps {
	const { t } = useTranslation(COMPLIANCE_NAMESPACE)
	const queryClient = useQueryClient()
	const [values, setValues] = useState(INITIAL)
	const [errors, setErrors] = useState<Errors>({})
	const [message, setMessage] = useState<string | null>(null)
	const create = useMutation({
		mutationFn: complianceService.createErasure,
		onSuccess: (created) => {
			setValues(INITIAL)
			void queryClient.invalidateQueries({ queryKey: ERASURES_KEY })
			onCreated(created.id)
		},
		onError: (error) => {
			const view = complianceError(error, t)
			setMessage(view.message)
			setErrors(view.fields)
		},
	})
	return {
		t,
		values,
		errors,
		message,
		submitting: create.isPending,
		basisOptions: Object.values(ErasureBasis).map((basis) => ({ id: basis, label: t(`basis.${basis}`) })),
		onChange: (field, value) => setValues((current) => ({ ...current, [field]: value })),
		onSubmit: () => {
			const found = validate(values, t)
			setErrors(found)
			setMessage(null)
			if (Object.keys(found).length > 0) return
			const note = values.note.trim()
			create.mutate({
				customerId: values.customerId.trim(),
				basis: values.basis as ErasureBasis,
				graceDays: Number(values.graceDays),
				...(note ? { note } : {}),
			})
		},
	}
}
